/**
 * PlanAutoDistributePanel — LK parity с MIS: ИИ-разнесение выбранных услуг по этапам.
 */
import { useEffect, useState } from 'react'
import type { AuthSession } from '../../../../types/plans'
import type { ConstructorPlan, ConstructorStage } from '../constructorTypes'
import { parsePlanVoiceCommand, opToHuman, type PlanVoiceOp } from '../voice/schema/planVoiceOps'
import type { MisImportedPlanSummary, SelectedPlanService } from './selectedPlanServices'
import { MisImportedPlansSidebar } from './MisImportedPlansSidebar'
import {
  WORKFLOW_CHECKPOINT_KEYS,
  formatCheckpointSavedAt,
  savePlanWorkflowCheckpoint,
  type PlanWorkflowCheckpoint,
} from '../workflowCheckpoint'

const AUTO_ACTION = 'plan_stage_auto_distribute'
const REFINE_ACTION = 'constructor_voice_compose'
const UI_SURFACE = 'constructor_auto_distribute_panel'
const PLAN_AUTO_DISTRIBUTE_MODEL_CHOICE = 'gemini_2_5_flash' as const

type FlowStep = 'idle' | 'proposing' | 'preview' | 'applying' | 'done' | 'error'

function buildPlanContext(
  plan: ConstructorPlan,
  stages: ConstructorStage[],
  patientName: string,
): Record<string, unknown> {
  const stages_summary = stages
    .map((s, i) => `${i + 1}. «${s.name ?? s.title ?? 'Этап'}» (${s.services?.length ?? 0} услуг)`)
    .join('; ')
  return {
    patient_name: patientName,
    plan_id: plan.mis_plan_id ?? String(plan.id),
    plan_title: plan.title ?? `план #${plan.id}`,
    stages_summary,
    plan_version: plan.version ?? null,
  }
}

function buildAutoDistributePrompt(selected: SelectedPlanService[]): string {
  const lines = selected.map(
    (s) =>
      `- ${s.display_name} (plan=${s.source_plan_id}, item=${s.item_id}, snapshot=${s.snapshot_id}, зуб=${s.tooth ?? '-'}, qty=${s.quantity})`,
  )
  return [
    'Разнеси выбранные услуги по клиническим этапам согласно правилам этапности.',
    'Верни только add_stage операции с корректными source_plan_id и snapshot_ids.',
    'Выбранные позиции:',
    ...lines,
  ].join('\n')
}

function OpCard({ op, onRemove }: { op: PlanVoiceOp; onRemove: () => void }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-2">
      <span className="mt-0.5 shrink-0 rounded bg-slate-100 px-1 py-0.5 text-[10px] font-semibold uppercase text-slate-500">
        {op.op.replace('_', ' ')}
      </span>
      <span className="min-w-0 flex-1 text-xs leading-snug text-slate-700">{opToHuman(op)}</span>
      <button type="button" className="text-xs text-slate-400 hover:text-slate-600" onClick={onRemove}>
        ×
      </button>
    </div>
  )
}

function VoiceMicButton({
  onCaptured,
  disabled,
}: {
  onCaptured: (f: File) => void
  disabled?: boolean
}) {
  const [recording, setRecording] = useState(false)
  const recorderRef = { current: null as MediaRecorder | null }
  const chunksRef = { current: [] as Blob[] }

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      chunksRef.current = []
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop())
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' })
        onCaptured(new File([blob], 'voice.webm', { type: blob.type }))
        setRecording(false)
      }
      recorder.start()
      recorderRef.current = recorder
      setRecording(true)
    } catch {
      setRecording(false)
    }
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => (recording ? recorderRef.current?.stop() : void start())}
      className={`rounded-md px-3 py-1.5 text-xs ${recording ? 'bg-red-50 text-red-600' : 'bg-slate-100'}`}
    >
      {recording ? '■ Стоп' : '🎙 Голос'}
    </button>
  )
}

export function PlanAutoDistributePanel({
  plan,
  stages,
  session,
  apiBase,
  patientName,
  misPatientId,
  misImportedSummaries,
  onApplied,
}: {
  plan: ConstructorPlan
  stages: ConstructorStage[]
  session: AuthSession
  apiBase: string
  patientName: string
  misPatientId: string | null
  misImportedSummaries: MisImportedPlanSummary[]
  onApplied: () => void
}) {
  const [step, setStep] = useState<FlowStep>('idle')
  const [message, setMessage] = useState('')
  const [refineText, setRefineText] = useState('')
  const [proposalId, setProposalId] = useState<string | null>(null)
  const [ops, setOps] = useState<PlanVoiceOp[]>([])
  const [selectedServices, setSelectedServices] = useState<SelectedPlanService[]>([])
  const [restored, setRestored] = useState(false)

  const checkpointKey = WORKFLOW_CHECKPOINT_KEYS.autoDistribute
  const savedCheckpoint = plan.workflow_checkpoints?.[checkpointKey] ?? null

  useEffect(() => {
    if (restored || step !== 'idle') return
    const cp = savedCheckpoint
    if (!cp || !Array.isArray(cp.ops) || cp.ops.length === 0) return
    if (cp.step !== 'preview' && cp.step !== 'error') return
    if (Array.isArray(cp.selected_services)) setSelectedServices(cp.selected_services as SelectedPlanService[])
    if (typeof cp.proposal_id === 'string') setProposalId(cp.proposal_id)
    setOps(cp.ops as PlanVoiceOp[])
    setStep('preview')
    setRestored(true)
  }, [savedCheckpoint, restored, step])

  const persistCheckpoint = async (payload: PlanWorkflowCheckpoint) => {
    await savePlanWorkflowCheckpoint(apiBase, session, plan.id, checkpointKey, payload)
  }

  const planContext = buildPlanContext(plan, stages, patientName)
  const metadataBase = {
    consumer: 'staff_lk_ai',
    model_choice: PLAN_AUTO_DISTRIBUTE_MODEL_CHOICE,
    plan_context: planContext,
    selected_services: selectedServices,
  }

  const authHeader = { Authorization: `Bearer ${session.accessToken}` }

  const propose = async (inputText: string) => {
    if (!misPatientId) {
      setStep('error')
      setMessage('Пациент не связан с MIS — ИИ-разнесение недоступно.')
      return
    }
    setStep('proposing')
    void persistCheckpoint({ step: 'proposing', selected_services: selectedServices })
    try {
      const res = await fetch(`${apiBase}/staff/ai-assist/proposals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          action_key: AUTO_ACTION,
          ui_surface: UI_SURFACE,
          source_kind: 'text',
          input_text: inputText,
          patient_id: misPatientId,
          target_entity_id: plan.mis_plan_id ?? String(plan.id),
          metadata: metadataBase,
        }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      const data = (await res.json()) as { proposal: { proposal_id: string; proposed_payload: unknown } }
      const parsed = parsePlanVoiceCommand(data.proposal.proposed_payload)
      if (!parsed.command) {
        throw new Error(
          parsed.skipped.length > 0
            ? 'ИИ вернул частичный ответ — уточните запрос.'
            : 'ИИ вернул неожиданный формат разнесения',
        )
      }
      setProposalId(data.proposal.proposal_id)
      setOps(parsed.command.operations)
      setStep('preview')
      void persistCheckpoint({ step: 'preview', proposal_id: data.proposal.proposal_id, ops: parsed.command.operations, selected_services: selectedServices, last_error: null })
    } catch (e) {
      let err = String(e)
      if (misPatientId) {
        const recovered = await recoverAutoDistributeProposal({ apiBase, authHeader, misPatientId })
        if (recovered) {
          const parsed = parsePlanVoiceCommand(recovered.proposed_payload)
          if (parsed.command) {
            setProposalId(recovered.proposal_id)
            setOps(parsed.command.operations)
            setStep('preview')
            setMessage(
              err.includes('500') || err.includes('502')
                ? 'Запрос прервался, но черновик этапов уже готов в MIS — показан последний результат.'
                : 'Восстановлен последний черновик разнесения из MIS.',
            )
            void persistCheckpoint({
              step: 'preview',
              proposal_id: recovered.proposal_id,
              ops: parsed.command.operations,
              selected_services: selectedServices,
              last_error: null,
              recovered_after_error: true,
            })
            return
          }
        }
      }
      if (err.includes('500') || err.includes('502')) {
        err = 'Сервер не успел ответить (~2 мин) или вернул ошибку. Нажмите «Повторить» — включён rule-based fallback.'
      } else if (err.includes('неожиданный формат')) {
        err = 'ИИ не вернул этапы. Нажмите «Повторить» — будет rule-based разнесение по категориям.'
      }
      setStep('error')
      setMessage(err)
      void persistCheckpoint({ step: 'error', last_error: err, ops, selected_services: selectedServices, proposal_id: proposalId })
    }
  }

  const refine = async (inputText: string) => {
    if (!misPatientId) return
    setStep('proposing')
    void persistCheckpoint({ step: 'proposing', selected_services: selectedServices })
    try {
      const res = await fetch(`${apiBase}/staff/ai-assist/proposals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          action_key: REFINE_ACTION,
          ui_surface: UI_SURFACE,
          source_kind: 'text',
          input_text: inputText,
          patient_id: misPatientId,
          target_entity_id: plan.mis_plan_id ?? String(plan.id),
          metadata: {
            ...metadataBase,
            previous_payload: { schema_version: '1', summary: '', operations: ops },
          },
        }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      const data = (await res.json()) as { proposal: { proposal_id: string; proposed_payload: unknown } }
      const parsed = parsePlanVoiceCommand(data.proposal.proposed_payload)
      if (!parsed.command) throw new Error('Не удалось уточнить черновик')
      setProposalId(data.proposal.proposal_id)
      setOps(parsed.command.operations)
      setRefineText('')
      setStep('preview')
      void persistCheckpoint({ step: 'preview', proposal_id: data.proposal.proposal_id, ops: parsed.command.operations, selected_services: selectedServices, last_error: null })
    } catch (e) {
      const err = String(e)
      setStep('error')
      setMessage(err)
      void persistCheckpoint({ step: 'error', last_error: err, ops, selected_services: selectedServices, proposal_id: proposalId })
    }
  }

  const transcribe = async (file: File) => {
    const fd = new FormData()
    fd.set('action_key', REFINE_ACTION)
    fd.set('ui_surface', UI_SURFACE)
    fd.set('patient_id', misPatientId ?? '')
    fd.set('audio', file)
    const res = await fetch(`${apiBase}/staff/ai-assist/voice/transcribe`, {
      method: 'POST',
      headers: authHeader,
      body: fd,
    })
    if (!res.ok) throw new Error(`${res.status}`)
    const data = (await res.json()) as { transcription: { text: string } }
    const text = data.transcription.text.trim()
    if (!text) throw new Error('Пустая транскрипция')
    setRefineText(text)
    await refine(`Уточни черновик этапов: ${text}`)
  }

  const confirm = async () => {
    if (!proposalId) return
    setStep('applying')
    try {
      const res = await fetch(`${apiBase}/staff/ai-assist/proposals/${proposalId}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({
          apply_immediately: true,
          note: `auto_distribute ops: ${ops.map((o) => o.op).join(', ')}`,
        }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      setStep('done')
      onApplied()
      setTimeout(() => setStep('idle'), 2000)
    } catch (e) {
      setStep('error')
      setMessage(String(e))
    }
  }

  const disabled = selectedServices.length === 0

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Выберите позиции из импортированных MIS-планов слева в блоке ниже, затем запустите ИИ-разнесение.
      </p>

      <MisImportedPlansSidebar
        apiBase={apiBase}
        session={session}
        summaries={misImportedSummaries}
        selectedServices={selectedServices}
        onSelectionChange={setSelectedServices}
      />

      <p className="text-xs text-slate-500">
        Выбрано позиций: <strong>{selectedServices.length}</strong>
      </p>

      {(step === 'idle' || step === 'error') && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => void propose(buildAutoDistributePrompt(selectedServices))}
          className="w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          Разнести по этапам (ИИ)
        </button>
      )}

      {step === 'proposing' && (
        <p className="text-xs text-slate-500">ИИ (Gemini Flash) формирует черновик этапов… Обычно 15–40 секунд.</p>
      )}
      {savedCheckpoint && (savedCheckpoint.step === 'preview' || savedCheckpoint.step === 'error') ? (
        <p className="rounded-md border border-violet-200 bg-violet-50 px-2 py-1 text-[11px] text-violet-900">
          Сохранено разнесение от {formatCheckpointSavedAt(savedCheckpoint) ?? 'ранее'}.
        </p>
      ) : null}
      {step === 'error' && (
        <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="text-xs text-amber-900">{message}</p>
          <p className="text-[11px] text-amber-800">
            Этапы появятся <strong>в этом блоке</strong> карточками «Добавить этап …», затем «Применить».
            До применения они не видны в центральной колонке конструктора.
          </p>
          <button
            type="button"
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
            disabled={disabled}
            onClick={() => void propose(buildAutoDistributePrompt(selectedServices))}
          >
            Повторить разнесение
          </button>
        </div>
      )}

      {step === 'preview' && (
        <>
          {message ? <p className="text-xs text-sky-800">{message}</p> : null}
          <p className="text-xs font-medium text-slate-700">Черновик этапов — проверьте и нажмите «Применить»</p>
          <div className="space-y-1.5">
            {ops.map((op, i) => (
              <OpCard key={i} op={op} onRemove={() => setOps((prev) => prev.filter((_, j) => j !== i))} />
            ))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void confirm()}
              className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white"
            >
              Применить
            </button>
            <button type="button" onClick={() => setStep('idle')} className="text-xs text-slate-500 underline">
              Отмена
            </button>
          </div>
          <div className="space-y-2 border-t border-slate-200 pt-2">
            <p className="text-xs font-medium text-slate-700">Уточнить черновик</p>
            <textarea
              value={refineText}
              onChange={(e) => setRefineText(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-slate-200 p-2 text-xs"
              placeholder="Например: терапию разбей на 2 визита"
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={!refineText.trim()}
                onClick={() => void refine(`Уточни черновик этапов: ${refineText.trim()}`)}
                className="rounded-md bg-slate-100 px-3 py-1.5 text-xs"
              >
                Уточнить
              </button>
              <VoiceMicButton onCaptured={(f) => void transcribe(f)} />
            </div>
          </div>
        </>
      )}

      {step === 'applying' && <p className="text-xs text-slate-500">Применяем этапы к плану…</p>}
      {step === 'done' && <p className="text-xs text-emerald-700">Этапы применены к плану.</p>}
    </div>
  )
}

async function recoverAutoDistributeProposal(params: {
  apiBase: string
  authHeader: Record<string, string>
  misPatientId: string
}): Promise<{ proposal_id: string; proposed_payload: unknown } | null> {
  const res = await fetch(
    `${params.apiBase}/staff/ai-assist/patients/${encodeURIComponent(params.misPatientId)}/proposals?limit=5`,
    { headers: params.authHeader },
  )
  if (!res.ok) return null
  const data = (await res.json()) as { items?: Array<Record<string, unknown>> }
  for (const row of data.items ?? []) {
    const meta = row.metadata_payload as Record<string, unknown> | null
    if (meta?.action_key === 'plan_stage_auto_distribute' || meta?.ui_surface === 'constructor_auto_distribute_panel') {
      return {
        proposal_id: String(row.proposal_id ?? ''),
        proposed_payload: row.proposed_payload ?? row.override_payload,
      }
    }
  }
  return null
}
